// ============================================================================
// What the browser can do about push alerts (RIZ132), as pure functions and
// the constants the page and the service worker agree on.
//
// Laura (Rize Realty): "push notifications for phone/email/texting ... we need to
// be able to communicate with ease and not miss anything." The subscribe flow
// that acts on these answers is pushAlerts.ts; this file is only the part that
// can be decided without a browser, a service worker or a database.
//
// NOTHING IS IMPORTED HERE, ON PURPOSE. Every rule below is judged from a
// user-agent string, a handful of feature checks or a byte array, so a module
// that reached for the Supabase client (or the `@/` alias) could not be loaded
// by `node --test` at all, and these are the rules most worth pinning: a wrong
// answer is either a button that does nothing on an iPhone or an alert that
// never arrives. notificationPreferences.ts is built the same way, for the same
// reason; the part that talks to the browser and the database lives next door.
// ============================================================================

/**
 * Whether this browser can show a push alert.
 *  - 'supported': it has a service worker, PushManager and Notification.
 *  - 'ios-needs-home-screen': an iPhone or iPad running Rize OS in Safari. iOS gives Web Push to Home
 *    Screen apps only (16.4 and later), so the person has to add Rize OS to the Home Screen first.
 *  - 'ios-needs-update': an iPhone or iPad running Rize OS as a Home Screen app that has no PushManager.
 *    That is iOS before 16.4, where the Home Screen app has a service worker and neither PushManager nor
 *    Notification. The update is the one thing that helps: every iOS browser runs Safari's engine, so
 *    "try another browser" would send the person to the browser they are already in.
 *  - 'unsupported': anything else, so the card says so instead of offering a button that cannot work.
 */
export type AlertSupport = 'supported' | 'ios-needs-home-screen' | 'ios-needs-update' | 'unsupported'

/** What the page sends the active service worker to ask whether it can show an alert, and what it answers. */
export const PUSH_PING = 'rizeos:push-ping'
export const PUSH_PONG = 'rizeos:push-pong'

/** How long Turn on waits for the service worker to be ready before it says so in plain words. */
export const READY_TIMEOUT_MS = 10_000

/**
 * How long ONE ping waits for the worker's answer. Silence through PING_ATTEMPTS windows means the active
 * worker has no push handler: an older one, while a newer one waits for the person to accept the update, or
 * one whose push-sw.js failed to load. The worker does not answer from a standing start: one the browser has
 * stopped has to start before it can read the ping, which on a slow phone can take longer than this, so a
 * single window would call a sleeping worker an old one.
 */
export const PING_TIMEOUT_MS = 1_500

/**
 * How many times the ping is tried before the worker is refused. The first wakes a stopped worker and the
 * second is what it answers, so the worst case is PING_ATTEMPTS x PING_TIMEOUT_MS, about 3 s; a worker that
 * answers the first is asked once.
 */
export const PING_ATTEMPTS = 2

/**
 * How long turning off waits for the delete of this device's row. The sign-out waits for turning off, and
 * supabase-js's fetch has no timeout of its own. The delete is not what stops the alerts: the browser is
 * unsubscribed before it, so the push service answers the next alert with a 410 and the drain drops the row.
 * A delete that has not answered in this long is let go, and is not a failure.
 */
export const DELETE_TIMEOUT_MS = 3_000

/**
 * How long the browser's unsubscribe() is waited for: when turning off, on the signed-out event, and when Turn
 * on drops a subscription made with another key. It is the browser's own call, with no limit of its own, and a
 * push service that never answers must not hang the card or the sign-out (Task 8's review: nothing hangs the
 * card but the permission prompt, which is the person's). One that has not settled by then counts as a failed
 * unsubscribe: turning off still deletes this person's row, then says so.
 */
export const UNSUBSCRIBE_TIMEOUT_MS = 3_000

/**
 * How long Turn on waits for pushManager.subscribe(), counted from after the person answered the permission
 * prompt (the prompt is theirs and is never timed). A browser whose push service never answers would otherwise
 * leave Turn on spinning for ever.
 */
export const SUBSCRIBE_TIMEOUT_MS = 15_000

/** The sentence for a worker that did not answer the ping. The card recognises it by this text. */
export const UPDATE_WAITING_MESSAGE = 'Rize OS has an update waiting. Refresh, then turn on alerts.'

const IOS_USER_AGENT = /iPad|iPhone|iPod/

/**
 * iPadOS 13 and later reports itself as a Mac (its user agent and its platform), and gives itself away
 * only by having a touch screen, which no Mac has. The same rule NOC266 shipped.
 */
function isIpadSendingMacs(nav: Navigator): boolean {
  return nav.platform === 'MacIntel' && nav.maxTouchPoints > 1
}

/**
 * Whether push can work here, read from the browser it is given (the real one by default; a test passes
 * its own). The iOS rule comes first, because Safari on an iPhone does not expose PushManager outside
 * the Home Screen app: that browser is told to add the app, not that it is unsupported. A Home Screen
 * app that then has no PushManager is told to update, not that it is unsupported either: it is iOS
 * before 16.4. Anything else that cannot push, on iOS or off it, is 'unsupported'; a Home Screen app
 * that has a PushManager and lacks something else is not a version problem and is not guessed at.
 */
export function alertSupport(env: { nav: Navigator; win: Window } = { nav: navigator, win: window }): AlertSupport {
  const { nav, win } = env
  const ios = IOS_USER_AGENT.test(nav.userAgent) || isIpadSendingMacs(nav)
  // display-mode is the standard; navigator.standalone is how older iOS Safari says it
  const standalone = !!win.matchMedia?.('(display-mode: standalone)').matches
    || (nav as Navigator & { standalone?: boolean }).standalone === true
  if (ios && !standalone) return 'ios-needs-home-screen'
  if ('serviceWorker' in nav && 'PushManager' in win && 'Notification' in win) return 'supported'
  return ios && !('PushManager' in win) ? 'ios-needs-update' : 'unsupported'
}

/** A VAPID public key (base64url, no padding) as the bytes pushManager.subscribe wants. Throws on a string that is not base64. */
export function urlBase64ToUint8Array(s: string): Uint8Array<ArrayBuffer> {
  const b64 = (s + '='.repeat((4 - (s.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/')
  return Uint8Array.from(atob(b64), (c) => c.charCodeAt(0))
}

/**
 * Whether a subscription was made with this key. `a` is the subscription's options.applicationServerKey,
 * null for one made without a key. A key that rotated, or a subscription made under another environment,
 * is not the same, and a subscription made with the old key can never receive the new key's alerts.
 */
export function sameKey(a: ArrayBuffer | null | undefined, b64: string): boolean {
  if (!a) return false
  const x = new Uint8Array(a)
  const y = urlBase64ToUint8Array(b64)
  return x.length === y.length && x.every((v, i) => v === y[i])
}

/** The most characters push_subscriptions.user_agent holds (its CHECK, mig 480). */
const USER_AGENT_MAX = 512

const IPAD_HINT = ' (iPad)'

/**
 * The user agent to save with a device (save_push_subscription's p_user_agent). It is the browser's own,
 * cut to the column, except that an iPad sending a Mac's user agent gets "(iPad)" on the end: otherwise
 * the device list would name it "Safari on Mac", beside the real Mac it is not. Only describeDevice reads it.
 */
export function deviceUserAgent(nav: Navigator = navigator): string {
  const userAgent = nav.userAgent ?? ''
  if (!isIpadSendingMacs(nav) || IOS_USER_AGENT.test(userAgent)) return userAgent.slice(0, USER_AGENT_MAX)
  return userAgent.slice(0, USER_AGENT_MAX - IPAD_HINT.length) + IPAD_HINT
}

const UNKNOWN_DEVICE = 'Unknown device'

/**
 * A saved device named the way a person would say it in the "Your devices" list: 'iPhone', 'iPad',
 * 'Android phone', or '<Chrome|Edge|Firefox|Safari> on <Mac|Windows|Linux|Chromebook>'. Phones and tablets
 * are named by the device (the browser on an iPhone is not what anyone calls it); a computer by its
 * browser and system. Anything this cannot place is 'Unknown device', never a guess.
 */
export function describeDevice(userAgent: string | null): string {
  const ua = userAgent?.trim() ?? ''
  if (ua === '') return UNKNOWN_DEVICE
  if (/iPhone|iPod/.test(ua)) return 'iPhone'
  if (/iPad/.test(ua)) return 'iPad'
  // before the Linux check below: an Android user agent says Linux too
  if (/Android/.test(ua)) return 'Android phone'
  const system = /CrOS/.test(ua) ? 'Chromebook'
    : /Windows/.test(ua) ? 'Windows'
    : /Macintosh|Mac OS X/.test(ua) ? 'Mac'
    : /Linux|X11/.test(ua) ? 'Linux'
    : null
  // Edge's and Chrome's user agents both say Chrome, and Chrome's says Safari: most specific first
  const browser = /Edg(?:e|A|iOS)?\//.test(ua) ? 'Edge'
    : /Firefox\/|FxiOS\//.test(ua) ? 'Firefox'
    : /Chrome\/|Chromium\/|CriOS\//.test(ua) ? 'Chrome'
    : /Safari\//.test(ua) ? 'Safari'
    : null
  return system && browser ? `${browser} on ${system}` : UNKNOWN_DEVICE
}
